import {makeAutoObservable, runInAction} from "mobx";
import {collection, onSnapshot, query, where, Unsubscribe, doc} from "firebase/firestore";
import {db} from "../Firebase";
import { FocusStore } from "./FocusStore";
import {
    AssetDeleted,
    AssetProjectId,
    AssetType,
    kAssetsCollectionName, kSequencesCollectionName, kCompositionsCollectionName,
    kTracksCollectionName, SequenceDeleted, SequenceProjectId,
    TrackDeleted,
    TrackProjectId,
    CompositionProjectId, CompositionDeleted,
    kProjectsCollectionName, kSpeakersCollectionName, SpeakerProjectId,
    kFoldersCollectionName, FolderProjectId, FolderDeleted,
    WorkspaceId,
    TrackIsBackup, TrackIsPlaceholder
} from "../utils/CollectionConstants";
import {IsExportedEdit, IsUploadedTrackType} from "../utils/utils";

type SubscribeOptions = {
    // Optional: used to scope queries and avoid showing data from other workspaces.
    workspaceId?: string;
    onAuthError?: () => void;
    // Optional: if provided, enables progressive focus chunk loading from the server.
    // Called only when Firestore indicates new chunks are published.
    getFocusChunk?: (args: { trackId: string; chunkId: number }) => Promise<any | undefined>;
};

class ProjectAssetsStoreImpl {
    projectId: string | null = null;
    projectName: string | null = null;
    projectCurrentCompositionId: string | null = null;
    projectDefaultTranscriptionLanguage: string = "auto";
    projectShowWordlessAudioEventsOnScript: boolean = false;
    projectImages: any[] = [];
    projectTracks: any[] = [];
    projectSequences: any[] = [];
    projectCompositions: any[] = [];
    projectSpeakers: any[] = [];
    projectFolders: any[] = [];
    loadingImages = false;
    loadingTracks = false;
    loadingSequences = false;
    loadingCompositions = false;
    loadingSpeakers = false;
    loadingFolders = false;

    // Optimistic tracks inserted immediately on upload initiation so the UI responds instantly,
    // even before Firestore snapshots reflect the new track.
    optimisticTracksById: Map<string, any> = new Map();

    private unsubImages: Unsubscribe | null = null;
    private unsubTracks: Unsubscribe | null = null;
    private unsubSequences: Unsubscribe | null = null;
    private unsubCompositions: Unsubscribe | null = null;
    private unsubSpeakers: Unsubscribe | null = null;
    private unsubFolders: Unsubscribe | null = null;
    private unsubProject: Unsubscribe | null = null;
    private unsubTrackFocusByTrackId: Map<string, Unsubscribe> = new Map();
    private focusChunkFetchInFlightByTrackId: Map<string, Set<number>> = new Map();

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    upsertOptimisticTrack(track: any) {
        const id = String(track?.id || track?.trackId || '');
        if (!id) return;

        const merged = {
            ...(this.optimisticTracksById.get(id) ?? {}),
            ...track,
            id,
        };

        this.optimisticTracksById.set(id, merged);

        // Also reflect immediately in the live list so the UI updates without waiting for snapshots.
        // This will be reconciled in the next tracks snapshot (which will remove the optimistic entry).
        const existingIdx = this.projectTracks.findIndex((t: any) => String(t?.id || t?.trackId || '') === id);
        if (existingIdx >= 0) {
            const next = [...this.projectTracks];
            next[existingIdx] = { ...(next[existingIdx] ?? {}), ...merged };
            this.projectTracks = next;
        } else {
            this.projectTracks = [merged, ...this.projectTracks];
        }
    }

    removeOptimisticTrack(trackId: string | null | undefined) {
        if (!trackId) return;
        const id = String(trackId);
        this.optimisticTracksById.delete(id);
    }

    subscribe(projectId: string | null | undefined, opts: SubscribeOptions = {}) {
        if (!projectId) {
            this.clear();
            this.teardown();
            this.projectId = null;
            this.projectName = null;
            return;
        }

        this.teardown();
        // Switching projects: clear previous data immediately so UI doesn't show stale state.
        this.clear();
        this.projectId = projectId;

        // Project document (for name)
        const projectRef = doc(db, kProjectsCollectionName, projectId);
        this.unsubProject = onSnapshot(projectRef, (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                runInAction(() => {
                    this.projectName = data?.name || null;
                    this.projectCurrentCompositionId =
                        typeof data?.currentCompositionId === "string" ? data.currentCompositionId : null;
                    this.projectDefaultTranscriptionLanguage =
                        typeof data?.defaultTranscriptionLanguage === "string" && String(data.defaultTranscriptionLanguage).trim()
                            ? String(data.defaultTranscriptionLanguage).trim()
                            : "auto";
                    this.projectShowWordlessAudioEventsOnScript = Boolean(data?.showWordlessAudioEventsOnScript);
                });
            }
        }, (error) => {
            console.error('[ProjectAssetsStore] project subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
        });

        // Images
        this.loadingImages = true;
        const imagesConstraints: any[] = [
            where(AssetProjectId, "==", projectId),
            where(AssetDeleted, "==", false),
            where(AssetType, "==", "IMAGE_UPLOAD"),
        ];
        if (opts.workspaceId) imagesConstraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qImages = query(collection(db, kAssetsCollectionName), ...imagesConstraints);
        this.unsubImages = onSnapshot(qImages, (snap) => {
            const images: any[] = [];
            snap.forEach((doc) => images.push({ id: doc.id, ...doc.data() }));
            console.log('[ProjectAssetsStore] images snapshot size', images.length);
            runInAction(() => {
                this.projectImages = images;
                this.loadingImages = false;
            });
        }, (error) => {
            console.error('[ProjectAssetsStore] images subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingImages = false; });
        });

        // Tracks
        this.loadingTracks = true;
        const constraints = [
            where(TrackProjectId, "==", projectId),
            where(TrackDeleted, "==", false),
        ];
        if (opts.workspaceId) constraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qTracks = query(collection(db, kTracksCollectionName), ...constraints);
        this.unsubTracks = onSnapshot(qTracks, (snap) => {
            const tracks: any[] = [];
            snap.forEach((doc) => {
                const data = doc.data();
                // Skip backup tracks - they are hidden from the UI
                // (backup tracks are LiveKit recordings used as fallback for failed local recordings)
                if (data[TrackIsBackup] === true || data[TrackIsPlaceholder] === true) {
                    return;
                }
                if (IsUploadedTrackType(data.trackType) || data.projectId === projectId || IsExportedEdit(data.trackType)) {
                    tracks.push({ id: doc.id, ...data });
                }
            });
            console.log('[ProjectAssetsStore] tracks snapshot size', tracks.length);
            runInAction(() => {
                // Merge any optimistic tracks that haven't appeared in Firestore yet.
                // Also drop optimistic entries once their real record is present.
                const seenIds = new Set(tracks.map((t: any) => String(t?.id || t?.trackId || '')));
                const seenAssetIds = new Set(tracks.map((t: any) => String(t?.assetId || '')));

                const optimistic = Array.from(this.optimisticTracksById.values()).filter(
                    (t: any) => String(t?.projectId || '') === String(projectId)
                );

                for (const opt of optimistic) {
                    const optId = String(opt?.id || opt?.trackId || '');
                    const optAssetId = String(opt?.assetId || '');
                    const alreadyPresent =
                        (optId && seenIds.has(optId)) ||
                        (optAssetId && seenAssetIds.has(optAssetId));

                    if (alreadyPresent) {
                        if (optId) this.optimisticTracksById.delete(optId);
                        continue;
                    }
                    tracks.unshift(opt);
                }

                this.projectTracks = tracks;
                console.log("ProjectAssetsStore tracks: ", this.projectTracks, "")
                this.loadingTracks = false;
            });

            // Best-effort: subscribe to per-track focus analysis docs so layouts/default placement
            // can use quickSummary without polling.
            try {
                const nextIds = new Set<string>();
                for (const t of tracks) {
                    const trackId = String(t?.id || t?.trackId || "");
                    if (!trackId) continue;
                    const hasVideo = Boolean(t?.hasVideoStream) || String(t?.filetype || "") === "video";
                    if (!hasVideo) continue;

                    nextIds.add(trackId);
                    if (this.unsubTrackFocusByTrackId.has(trackId)) continue;

                    const focusRef = doc(db, kTracksCollectionName, trackId, "analysis", "focus");
                    const unsub = onSnapshot(
                        focusRef,
                        (focusSnap) => {
                            if (!focusSnap.exists()) return;
                            const data: any = focusSnap.data();
                            FocusStore.setAnalysisDocFromFirestore({ trackId, data });

                            const getFocusChunk = opts.getFocusChunk;
                            if (!getFocusChunk) return;

                            const publishedChunkCount = Math.max(0, Number(data?.publishedChunkCount ?? 0) || 0);
                            if (!(publishedChunkCount > 0)) return;

                            let inFlight = this.focusChunkFetchInFlightByTrackId.get(trackId);
                            if (!inFlight) {
                                inFlight = new Set<number>();
                                this.focusChunkFetchInFlightByTrackId.set(trackId, inFlight);
                            }

                            for (let i = 0; i < publishedChunkCount; i++) {
                                if (FocusStore.hasChunk(trackId, i)) continue;
                                if (inFlight.has(i)) continue;
                                inFlight.add(i);

                                void (async () => {
                                    try {
                                        const chunkJson = await getFocusChunk({ trackId, chunkId: i });
                                        if (chunkJson) {
                                            FocusStore.setChunk({ trackId, chunkIndex: i, chunk: chunkJson });
                                        }
                                    } catch {
                                        // best-effort; will retry on next Firestore update
                                    } finally {
                                        try {
                                            const cur = this.focusChunkFetchInFlightByTrackId.get(trackId);
                                            cur?.delete(i);
                                            if (cur && cur.size === 0) this.focusChunkFetchInFlightByTrackId.delete(trackId);
                                        } catch {}
                                    }
                                })();
                            }
                        },
                        () => {},
                    );

                    this.unsubTrackFocusByTrackId.set(trackId, unsub);
                }

                // Unsubscribe for removed tracks.
                for (const [trackId, unsub] of this.unsubTrackFocusByTrackId.entries()) {
                    if (nextIds.has(trackId)) continue;
                    try { unsub?.(); } catch {}
                    this.unsubTrackFocusByTrackId.delete(trackId);
                    try { FocusStore.clear(trackId); } catch {}
                    try { this.focusChunkFetchInFlightByTrackId.delete(trackId); } catch {}
                }
            } catch {}
        }, (error) => {
            console.error('[ProjectAssetsStore] tracks subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingTracks = false; });
        });

        this.loadingSequences = true;
        const sequenceConstraints: any[] = [
            where(SequenceProjectId, "==", projectId),
            where(SequenceDeleted, "==", false),
        ];
        if (opts.workspaceId) sequenceConstraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qSequences = query(collection(db, kSequencesCollectionName), ...sequenceConstraints);
        this.unsubSequences = onSnapshot(qSequences, (snap) => {
            const sequences: any[] = [];
            snap.forEach((doc) => sequences.push({ id: doc.id, ...doc.data() }));
            runInAction(() => {
                this.projectSequences = sequences;
                this.loadingSequences = false;
            });
        }, (error) => {
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingSequences = false; });
        });

        // Compositions
        this.loadingCompositions = true;
        const compositionConstraints: any[] = [
            where(CompositionProjectId, "==", projectId),
            where(CompositionDeleted, "==", false),
        ];
        if (opts.workspaceId) compositionConstraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qCompositions = query(collection(db, kCompositionsCollectionName), ...compositionConstraints);
        this.unsubCompositions = onSnapshot(qCompositions, (snap) => {
            const compositions: any[] = [];
            snap.forEach((doc) => compositions.push({ id: doc.id, ...doc.data() }));
            console.log('[ProjectAssetsStore] compositions snapshot size', compositions.length);
            runInAction(() => {
                this.projectCompositions = compositions;
                this.loadingCompositions = false;
            });
        }, (error) => {
            console.error('[ProjectAssetsStore] compositions subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingCompositions = false; });
        });


        // Speakers
        this.loadingSpeakers = true;
        const speakerConstraints: any[] = [
            where(SpeakerProjectId, "==", projectId),
        ];
        if (opts.workspaceId) speakerConstraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qSpeakers = query(collection(db, kSpeakersCollectionName), ...speakerConstraints);
        this.unsubSpeakers = onSnapshot(qSpeakers, (snap) => {
            const speakers: any[] = [];
            snap.forEach((doc) => speakers.push({ id: doc.id, ...doc.data() }));
            console.log('[ProjectAssetsStore] speakers snapshot size', speakers.length);
            runInAction(() => {
                this.projectSpeakers = speakers;
                this.loadingSpeakers = false;
            });
        }, (error) => {
            console.error('[ProjectAssetsStore] speaker subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingSpeakers = false; });
        });

        // Folders
        this.loadingFolders = true;
        const folderConstraints: any[] = [
            where(FolderProjectId, "==", projectId),
            where(FolderDeleted, "==", false),
        ];
        if (opts.workspaceId) folderConstraints.push(where(WorkspaceId, "==", opts.workspaceId));
        // @ts-ignore - spread dynamic constraints
        const qFolders = query(collection(db, kFoldersCollectionName), ...folderConstraints);
        this.unsubFolders = onSnapshot(qFolders, (snap) => {
            const folders: any[] = [];
            snap.forEach((doc) => folders.push({ id: doc.id, ...doc.data() }));
            console.log('[ProjectAssetsStore] folders snapshot size', folders.length);
            runInAction(() => {
                this.projectFolders = folders;
                this.loadingFolders = false;
            });
        }, (error) => {
            console.error('[ProjectAssetsStore] folders subscription error', error);
            if (error.code === 'permission-denied') opts.onAuthError?.();
            runInAction(() => { this.loadingFolders = false; });
        });
    }

    teardown() {
        this.unsubProject?.();
        this.unsubImages?.();
        this.unsubTracks?.();
        this.unsubSequences?.();
        this.unsubCompositions?.();
        this.unsubSpeakers?.();
        this.unsubFolders?.();
        for (const unsub of this.unsubTrackFocusByTrackId.values()) {
            try { unsub?.(); } catch {}
        }
        this.unsubTrackFocusByTrackId.clear();
        this.focusChunkFetchInFlightByTrackId.clear();
        this.unsubProject = null;
        this.unsubImages = null;
        this.unsubTracks = null;
        this.unsubSequences = null;
        this.unsubCompositions = null;
        this.unsubSpeakers = null;
        this.unsubFolders = null;
    }

    clear() {
        this.projectName = null;
        this.projectCurrentCompositionId = null;
        this.projectDefaultTranscriptionLanguage = "auto";
        this.projectShowWordlessAudioEventsOnScript = false;
        this.projectImages = [];
        this.projectTracks = [];
        this.projectSequences = [];
        this.projectCompositions = [];
        this.projectSpeakers = [];
        this.projectFolders = [];
        this.optimisticTracksById.clear();
        this.loadingImages = false;
        this.loadingTracks = false;
        this.loadingSequences = false;
        this.loadingCompositions = false;
        this.loadingFolders = false;
    }

    setProjectDefaultTranscriptionLanguage(languageCode: string) {
        const normalized = String(languageCode ?? "").trim() || "auto";
        this.projectDefaultTranscriptionLanguage = normalized;
    }

    setProjectShowWordlessAudioEventsOnScript(show: boolean) {
        this.projectShowWordlessAudioEventsOnScript = Boolean(show);
    }
}

export const ProjectAssetsStore = new ProjectAssetsStoreImpl();
